import { Link, useLocation } from "react-router-dom";
import { Home, Calendar, Search, Settings, MessageSquare, Clock, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useUserCommunity } from "@/hooks/useUserCommunity";

const getCustomerNav = (communitySlug?: string | null) => [
  { href: "/", icon: Home, label: "Home" },
  { href: "/bookings", icon: Calendar, label: "Bookings" },
  { href: communitySlug ? `/b/${communitySlug}` : "/explore", icon: Search, label: "Explore" },
  { href: "/settings", icon: Settings, label: "Settings" },
];

const staffNav = [
  { href: "/", icon: Home, label: "Home" },
  { href: "/staff", icon: LayoutDashboard, label: "Dashboard" },
  { href: "/staff/availability", icon: Clock, label: "Schedule" },
  { href: "/staff/profile", icon: Settings, label: "Settings" },
];

const businessAdminNav = [
  { href: "/", icon: Home, label: "Home" },
  { href: "/business", icon: LayoutDashboard, label: "Dashboard" },
  { href: "/business/calendar", icon: Calendar, label: "Calendar" },
  { href: "/business/settings", icon: Settings, label: "Settings" },
];

const superAdminNav = [
  { href: "/", icon: Home, label: "Home" },
  { href: "/admin", icon: LayoutDashboard, label: "Dashboard" },
  { href: "/admin/tickets", icon: MessageSquare, label: "Tickets" },
  { href: "/admin/settings", icon: Settings, label: "Settings" },
];

export function MobileNav() {
  const location = useLocation();
  const { isStaff, isBusinessAdmin, isSuperAdmin } = useAuth();
  const { data: userCommunity } = useUserCommunity();

  const isCustomer = !isSuperAdmin && !isBusinessAdmin && !isStaff;

  let navItems = getCustomerNav(userCommunity?.slug);
  if (isSuperAdmin) {
    navItems = superAdminNav;
  } else if (isBusinessAdmin) {
    navItems = businessAdminNav;
  } else if (isStaff) {
    navItems = staffNav;
  }

  // Find the longest matching nav item path to highlight only one item
  const getActiveHref = () => {
    if (location.pathname === "/") return "/";
    
    const matches = navItems
      .filter(item => item.href !== "/" && location.pathname.startsWith(item.href))
      .sort((a, b) => b.href.length - a.href.length);
    
    return matches.length > 0 ? matches[0].href : null;
  };

  const activeHref = getActiveHref();

  return (
    <nav className="fixed bottom-3 left-4 right-4 z-50 lg:hidden">
      <div className="mx-auto max-w-md rounded-2xl bg-card/60 backdrop-blur-2xl shadow-xl border border-white/10">
        <div className="flex items-center justify-around px-2 py-2">
          {navItems.map((item) => {
            const isActive = item.href === activeHref;
            const showLogo = isCustomer && item.label === "Explore" && userCommunity?.logo_url;

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex flex-col items-center gap-1 px-4 py-2 text-xs font-medium rounded-xl transition-all duration-200",
                  isActive
                    ? "bg-primary/20 text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {showLogo ? (
                  <img
                    src={userCommunity.logo_url!}
                    alt={userCommunity.name}
                    className="h-[22px] w-[22px] rounded-full object-cover"
                  />
                ) : (
                  <item.icon className="h-[22px] w-[22px]" />
                )}
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
